import { buildApiUrl } from "../utils/apiConfig";
import { fetchExplorationHomeCompletion } from "../services/athleteHomeCompletionService";
import { fetchMediaKit } from "../services/mediaKitService";
import type { NcaaTrackerPayload } from "../services/ncaaTrackerApi";
import type { AthleteHomeActionId } from "./athleteHomeActions";

export { markExplorationHomeActionComplete as markAthleteHomeActionVisited } from "../services/athleteHomeCompletionService";

export type AthleteHomeCompletionMap = Record<AthleteHomeActionId, boolean>;

export type AthleteHomeCompletionContext = {
    profileId: number;
    athleteUserId?: number;
    username: string;
    athleteProfile: Record<string, unknown>;
    isAdminViewingAthlete: boolean;
    adminAthleteProfileId?: string;
};

function hasText(value: unknown): boolean {
    return typeof value === "string" && value.trim().length > 0;
}

export function isAthleteProfileComplete(profile: Record<string, unknown>): boolean {
    const sport =
        (typeof profile.sport === "object" && profile.sport !== null && "name" in profile.sport
            ? (profile.sport as { name?: string }).name
            : profile.sport) ?? null;

    const hasSport = hasText(typeof sport === "string" ? sport : "");
    const hasGradYear = typeof profile.graduationYear === "number" && profile.graduationYear > 0;

    const detailFields = [
        profile.school,
        profile.location,
        profile.program,
        profile.athletePhone,
        profile.instagramHandle,
        profile.tiktokHandle,
        profile.linkedinUrl,
        profile.facebookName,
        profile.hudlUrl,
        profile.xHandle,
        profile.imageUrl,
        profile.profileImageUrl,
    ];

    const filledDetails = detailFields.filter(hasText).length;

    return hasSport && hasGradYear && filledDetails >= 2;
}

function ncaaPayloadHasProgress(payload: unknown): boolean {
    if (!payload || typeof payload !== "object") {
        return false;
    }

    const divisions = ["d1", "d2"] as const;
    for (const key of divisions) {
        const division = (payload as Record<string, unknown>)[key];
        if (!division || typeof division !== "object") {
            continue;
        }
        const sections = (division as { sections?: unknown }).sections;
        if (!Array.isArray(sections)) {
            continue;
        }
        for (const section of sections) {
            if (!section || typeof section !== "object") {
                continue;
            }
            const rows = (section as { rows?: unknown }).rows;
            if (!Array.isArray(rows)) {
                continue;
            }
            for (const row of rows) {
                if (!row || typeof row !== "object") {
                    continue;
                }
                const typed = row as { title?: string; grade?: string; credits?: string };
                if (hasText(typed.title) || hasText(typed.grade) || hasText(typed.credits)) {
                    return true;
                }
            }
        }
    }

    return false;
}

async function fetchJson(url: string): Promise<unknown | null> {
    try {
        const response = await fetch(url, {
            headers: { Accept: "application/json" },
            credentials: "include",
        });
        if (!response.ok) {
            return null;
        }
        return response.json();
    } catch {
        return null;
    }
}

async function fetchNcaaStarted(ctx: AthleteHomeCompletionContext): Promise<boolean> {
    let url: string;
    if (ctx.isAdminViewingAthlete && ctx.adminAthleteProfileId) {
        url = buildApiUrl(`/api/athletes/${encodeURIComponent(ctx.adminAthleteProfileId)}/ncaa-tracker`);
    } else {
        url = buildApiUrl("/api/athletes/me/ncaa-tracker");
    }

    const data = (await fetchJson(url)) as { payload?: NcaaTrackerPayload; updatedAt?: string | null } | null;
    if (!data) {
        return false;
    }
    if (data.updatedAt) {
        return true;
    }
    return ncaaPayloadHasProgress(data.payload);
}

async function fetchHasColleges(username: string): Promise<boolean> {
    const data = await fetchJson(buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(username)}`));
    return Array.isArray(data) && data.length > 0;
}

async function fetchHasCompletedNilCourse(): Promise<boolean> {
    const data = (await fetchJson(buildApiUrl("/api/nil-edu/courses"))) as {
        courses?: { isCompleted?: boolean }[];
    } | null;
    if (!data?.courses) {
        return false;
    }
    return data.courses.some((course) => course.isCompleted === true);
}

async function fetchMediaKitCompleted(athleteUserId?: number): Promise<boolean> {
    try {
        const kit = await fetchMediaKit(athleteUserId);
        return kit.completed === true;
    } catch {
        return false;
    }
}

export async function fetchAthleteHomeCompletion(
    ctx: AthleteHomeCompletionContext,
): Promise<AthleteHomeCompletionMap> {
    const [ncaa, colleges, nilCourse, mediaKit, exploration] = await Promise.all([
        fetchNcaaStarted(ctx),
        ctx.username ? fetchHasColleges(ctx.username) : Promise.resolve(false),
        fetchHasCompletedNilCourse(),
        fetchMediaKitCompleted(ctx.isAdminViewingAthlete ? ctx.athleteUserId : undefined),
        fetchExplorationHomeCompletion({
            isAdminViewingAthlete: ctx.isAdminViewingAthlete,
            adminAthleteProfileId: ctx.adminAthleteProfileId,
        }),
    ]);

    return {
        profile: isAthleteProfileComplete(ctx.athleteProfile),
        ncaa,
        "college-list": colleges,
        "nil-course": nilCourse,
        "media-kit": mediaKit,
        hub: exploration.hub,
        calendar: exploration.calendar,
    };
}

export function parseAthleteHomeContext(
    data: Record<string, unknown>,
    options: { isAdminViewingAthlete: boolean; adminAthleteProfileId?: string },
): AthleteHomeCompletionContext | null {
    const profileId = typeof data.id === "number" ? data.id : Number(data.id);
    if (!Number.isFinite(profileId)) {
        return null;
    }

    const username =
        (typeof data.email === "string" && data.email.trim()) ||
        (typeof data.username === "string" && data.username.trim()) ||
        "";

    return {
        profileId,
        athleteUserId: typeof data.userId === "number" ? data.userId : undefined,
        username,
        athleteProfile: data,
        isAdminViewingAthlete: options.isAdminViewingAthlete,
        adminAthleteProfileId: options.adminAthleteProfileId,
    };
}
